export type GovernorRuntimeDomain =
  | 'tree'
  | 'relationship'
  | 'messaging'
  | 'calls'
  | 'media'
  | 'membership'
  | 'auth'
  | 'routing'
  | 'deployment'
  | 'runtime'
  | 'verification'
  | 'repair'
  | 'learning'
  | 'command-center';

export type GovernorEvidenceSeverity = 'info' | 'warn' | 'fail';

export type GovernorRuntimeEvidenceEvent = {
  id: string;
  timestamp: string;
  domain: GovernorRuntimeDomain;
  source: string;
  severity: GovernorEvidenceSeverity;
  summary: string;
  evidence: string[];
  owner?: string;
  repairReady: boolean;
};

export type GovernorRuntimeEvidenceSnapshot = {
  generatedAt: string;
  totalEvents: number;
  failures: number;
  warnings: number;
  repairReady: number;
  events: GovernorRuntimeEvidenceEvent[];
};

const events: GovernorRuntimeEvidenceEvent[] = [];

const listeners =
  new Set<() => void>();

let revision = 0;

const notifyGovernorRuntimeEvidenceListeners =
  (): void => {
    revision += 1;

    for (const listener of listeners) {
      try {
        listener();
      } catch {
        // Evidence consumer notification failure is non-fatal.
      }
    }
  };

export function getGovernorRuntimeEvidenceRevision():
  number {
  return revision;
}

export function subscribeGovernorRuntimeEvidence(
  listener: () => void,
): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function publishGovernorRuntimeEvidence(
  event: Omit<GovernorRuntimeEvidenceEvent, 'id' | 'timestamp'>
): GovernorRuntimeEvidenceEvent {
  const full: GovernorRuntimeEvidenceEvent = {
    ...event,
    id: `gre-${Date.now()}-${events.length + 1}`,
    timestamp: new Date().toISOString(),
  };

  events.push(full);
  notifyGovernorRuntimeEvidenceListeners();
  return full;
}

export function getGovernorRuntimeEvidenceSnapshot(): GovernorRuntimeEvidenceSnapshot {
  const failures = events.filter((e) => e.severity === 'fail').length;
  const warnings = events.filter((e) => e.severity === 'warn').length;
  const repairReady = events.filter((e) => e.repairReady).length;

  return {
    generatedAt: new Date().toISOString(),
    totalEvents: events.length,
    failures,
    warnings,
    repairReady,
    events: [...events],
  };
}

export function clearGovernorRuntimeEvidenceBus(): void {
  events.length = 0;
  notifyGovernorRuntimeEvidenceListeners();
}

export const GOVERNOR_RUNTIME_EVIDENCE_BUS_VERSION = 'CP-021.1';
export const GOVERNOR_RUNTIME_EVIDENCE_BUS_DOCTRINE =
  'Every subsystem must publish runtime evidence before repair authorization. No blind repair is permitted. One Governor OS, One Command Center.';
